import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  ArrowLeft,
  Plus,
  Trash2,
  CalendarDays,
  MapPin,
  Route as RouteIcon,
  Utensils,
  Check,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { getEventoCarrera, crearInscripcionCarrera } from "@/lib/carrera.functions";
import {
  type Participante,
  participanteSchema,
  contactoSchema,
  euro,
  fechaEvento,
  modalidadLabels,
  edadEn,
  totalParticipantes,
  abierto,
} from "@/lib/carrera.shared";
import { enviarFormularioRedsys } from "@/lib/redsys-form";
import poster from "@/assets/san-silvestre-sierra-norte-definitivo.jpg.asset.json";
import reglamento from "@/assets/reglamento-san-silvestre-2026.pdf.asset.json";
import { FileText } from "lucide-react";
import { CarreraBeneficios } from "@/components/eventos/CarreraBeneficios";
export const Route = createFileRoute("/san-silvestre-sierra-norte")({
  loader: () => getEventoCarrera(),
  head: ({ loaderData }) => ({
    scripts: loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SportsEvent",
              name: loaderData.titulo,
              startDate: `${loaderData.fecha}T${loaderData.salida}:00+01:00`,
              endDate: `${loaderData.fecha}T${loaderData.cierre_meta}:00+01:00`,
              eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
              eventStatus: "https://schema.org/EventScheduled",
              location: {
                "@type": "Place",
                name: loaderData.recorrido,
                address: {
                  "@type": "PostalAddress",
                  addressRegion: "Guadalajara",
                  addressCountry: "ES",
                },
              },
              organizer: { "@type": "Organization", name: loaderData.organizador },
            }),
          },
        ]
      : [],
    meta: [
      { title: "San Silvestre de la Sierra Norte 2026 · Inscripciones" },
      {
        name: "description",
        content:
          "Carrera popular de 7 km de La Nava de Jadraque a El Ordial el 27 de diciembre. Corredores, andarines y acompañantes, con comida incluida.",
      },
      { property: "og:title", content: "I San Silvestre de la Sierra Norte · 27 diciembre 2026" },
      {
        property: "og:description",
        content:
           "7 km entre La Nava de Jadraque, Arroyo de las Fraguas y El Ordial. Comida incluida, premios, sorteo de regalos y una paletilla por cada 10 participantes del mismo club.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CarreraPage,
});
const nuevaPersona = (): Participante => ({
  nombre: "",
  modalidad: "corredor",
  fecha_nacimiento: "",
  grupo: "",
  menor: false,
  tutor: "",
  autorizacion: false,
});
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function FechaNacimiento({
  id,
  value,
  maxYear,
  onChange,
}: {
  id: string;
  value: string;
  maxYear: number;
  onChange: (v: string) => void;
}) {
  const [y0, m0, d0] = value ? value.split("-") : ["", "", ""];
  const [d, setD] = useState(d0 ? String(Number(d0)) : "");
  const [m, setM] = useState(m0 ? String(Number(m0)) : "");
  const [y, setY] = useState(y0 || "");
  function upd(nd: string, nm: string, ny: string) {
    setD(nd); setM(nm); setY(ny);
    if (nd && nm && ny) {
      const max = new Date(Number(ny), Number(nm), 0).getDate();
      const dd = Math.min(Number(nd), max);
      onChange(`${ny}-${nm.padStart(2, "0")}-${String(dd).padStart(2, "0")}`);
    } else onChange("");
  }
  const sel =
    "h-11 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const years = Array.from({ length: 100 }, (_, k) => maxYear - k);
  return (
    <div className="mt-2 grid grid-cols-[1fr_1.6fr_1.2fr] gap-2">
      <select id={id} aria-label="Día" className={sel} value={d} onChange={(ev) => upd(ev.target.value, m, y)}>
        <option value="">Día</option>
        {Array.from({ length: 31 }, (_, k) => (
          <option key={k} value={k + 1}>{k + 1}</option>
        ))}
      </select>
      <select aria-label="Mes" className={sel} value={m} onChange={(ev) => upd(d, ev.target.value, y)}>
        <option value="">Mes</option>
        {MESES.map((n, k) => (
          <option key={n} value={k + 1}>{n}</option>
        ))}
      </select>
      <select aria-label="Año" className={sel} value={y} onChange={(ev) => upd(d, m, ev.target.value)}>
        <option value="">Año</option>
        {years.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </div>
  );
}

function CarreraPage() {
  const e = Route.useLoaderData();
  const [step, setStep] = useState(0);
  const [ps, setPs] = useState<Participante[]>([nuevaPersona()]);
  const [contact, setContact] = useState({ nombre: "", email: "", telefono: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [website, setWebsite] = useState("");
  const create = useServerFn(crearInscripcionCarrera);
  const total = totalParticipantes(ps, e);
  const open = abierto(e);
  const edit = (i: number, patch: Partial<Participante>) => {
    setPs((v) => v.map((p, j) => (j === i ? { ...p, ...patch } : p)));
    setRequestId("");
  };
  function next() {
    setError("");
    const valid =
      step === 0
        ? ps.map((p) => participanteSchema.safeParse(p)).find((p) => !p.success)
        : contactoSchema.safeParse(contact);
    if (valid && !valid.success) {
      setError(valid.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    if (step === 0 && !contact.nombre.trim() && ps[0]?.nombre) {
      setContact((v) => ({ ...v, nombre: ps[0].nombre.trim() }));
    }
    setStep((v) => v + 1);
  }
  async function pay() {
    setError("");
    if (!accept) {
      setError("Acepta las condiciones para continuar");
      return;
    }
    setBusy(true);
    const id = requestId || crypto.randomUUID();
    setRequestId(id);
    try {
      const result = await create({
        data: { idempotencia: id, participantes: ps, contacto: contact, acepta: true, website },
      });
      enviarFormularioRedsys(result.form);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo iniciar el pago");
      setBusy(false);
    }
  }
  return (
    <>
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <p className="text-sm font-medium text-accent">LA NAVA DE JADRAQUE → EL ORDIAL</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-tight text-primary md:text-5xl">
            {e.titulo}
          </h1>
          <div className="mt-7 flex flex-wrap gap-5 text-sm">
            <span className="flex items-center gap-2">
              <CalendarDays size={18} />
              {fechaEvento(e.fecha)} · {e.salida} h
            </span>
            <span className="flex items-center gap-2">
              <RouteIcon size={18} />
              {e.distancia_km} km
            </span>
            <span className="flex items-center gap-2">
              <Utensils size={18} />
              Comida incluida
            </span>
          </div>
        </div>
      </section>
      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-10 md:grid-cols-[320px_minmax(0,1fr)] md:px-6 md:py-14">
        <aside>
          <a href={poster.url} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={poster.url}
              alt="Cartel completo de la I San Silvestre de la Sierra Norte"
              width={1024}
              height={1536}
              className="mx-auto h-auto w-full max-w-[320px] rounded-sm"
            />
            <span className="mt-3 flex items-center justify-center gap-2 text-xs text-primary">
              Ver cartel completo <ExternalLink size={13} />
            </span>
          </a>
          <a
            href={reglamento.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 rounded-sm border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-secondary"
          >
            <FileText size={16} /> Ver reglamento (PDF)
          </a>
          <div className="mt-7 space-y-4 text-sm">
            <p className="flex gap-2">
              <MapPin className="h-5 w-5 shrink-0 text-primary" />
              {e.recorrido}
            </p>
            <p>
              Cierre de meta: <strong>{e.cierre_meta} h</strong>
            </p>
            <div className="border-t pt-4">
              <p className="mb-2 font-medium">Horarios</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>Dorsales: 10:30–11:45 h, La Nava de Jadraque</li>
                <li>Salida: 12:00 h · Cierre de meta: 14:00 h</li>
                <li>Entrega de premios: desde 14:15 h</li>
                <li>Comida: desde 14:30 h, El Ordial</li>
                <li>Inscripciones hasta el 23 de diciembre, 23:59 h</li>
              </ul>
            </div>
            <CarreraBeneficios />
            <p className="border-t pt-4 text-muted-foreground">
              Organiza: {e.organizador}.<br />
              Con la colaboración de la Diputación de Guadalajara y los ayuntamientos de La Nava
              de Jadraque, Arroyo de las Fraguas y El Ordial.
            </p>
          </div>
        </aside>
        <section className="min-w-0" aria-label="Inscripción">
          <div className="flex items-end justify-between gap-3">
            <h2 className="font-serif text-3xl">Tu inscripción</h2>
            <span className="text-sm text-muted-foreground">
              {ps.length} {ps.length === 1 ? "persona" : "personas"}
            </span>
          </div>
          {!open && (
            <p className="mt-5 border-l-4 border-accent bg-secondary/50 p-4 text-sm">
              Próximamente abriremos las inscripciones. Puedes consultar las modalidades y preparar
              tu grupo.
            </p>
          )}
          <ol className="my-7 flex items-center gap-2">
            {["Participantes", "Contacto", "Pago"].map((s, i) => (
              <li key={s} className="flex flex-1 items-center gap-2">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${i <= step ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}
                >
                  {i < step ? <Check size={15} /> : i + 1}
                </span>
                <span
                  className={`text-xs sm:text-sm ${i === step ? "font-semibold text-foreground" : "text-muted-foreground"}`}
                >
                  {s}
                </span>
                {i < 2 && (
                  <span className={`h-px flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />
                )}
              </li>
            ))}
          </ol>
          {step === 0 && (
            <div className="space-y-5">
              {ps.map((p, i) => (
                <article
                  key={i}
                  className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
                >
                  <header className="flex items-center justify-between border-b bg-secondary/40 px-4 py-3 sm:px-6">
                    <h3 className="flex items-center gap-3 font-sans text-sm font-semibold">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                        {i + 1}
                      </span>
                      {p.nombre.trim() || `Persona ${i + 1}`}
                    </h3>
                    {ps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Eliminar persona ${i + 1}`}
                        title="Eliminar persona"
                        onClick={() => {
                          setPs((v) => v.filter((_, j) => i !== j));
                          setRequestId("");
                        }}
                      >
                        <Trash2 />
                      </Button>
                    )}
                  </header>
                  <div className="space-y-6 p-4 sm:p-6">
                    <fieldset>
                      <legend className="mb-3 text-sm font-medium">¿Cómo participa?</legend>
                      <div className="grid grid-cols-3 gap-2">
                        {(Object.keys(modalidadLabels) as (keyof typeof modalidadLabels)[]).map(
                          (mode) => (
                            <label
                              key={mode}
                              className={`relative flex cursor-pointer flex-col items-center rounded-lg border-2 px-2 py-3 text-center text-sm transition-colors ${p.modalidad === mode ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                            >
                              <input
                                type="radio"
                                className="sr-only"
                                name={`mode-${i}`}
                                checked={p.modalidad === mode}
                                onChange={() => edit(i, { modalidad: mode })}
                              />
                              <span className="text-xs font-medium sm:text-sm">{modalidadLabels[mode]}</span>
                              <span className="mt-1 text-lg font-semibold text-primary">
                                {euro(
                                  Number(
                                    mode === "acompanante"
                                      ? e.precio_acompanante
                                      : e.precio_participante,
                                  ),
                                )}
                              </span>
                              {p.modalidad === mode && (
                                <Check className="absolute right-1.5 top-1.5 h-4 w-4 text-primary" />
                              )}
                            </label>
                          ),
                        )}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Acompañante: solo comida, sin recorrido. Menores de 16 años: solo andarín.
                      </p>
                    </fieldset>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Label htmlFor={`name-${i}`}>Nombre y apellidos</Label>
                        <Input
                          id={`name-${i}`}
                          className="mt-2 h-11"
                          placeholder="Ej.: María García López"
                          value={p.nombre}
                          maxLength={120}
                          onChange={(ev) => edit(i, { nombre: ev.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`fnac-d-${i}`}>Fecha de nacimiento</Label>
                        <FechaNacimiento
                          id={`fnac-d-${i}`}
                          value={p.fecha_nacimiento}
                          maxYear={Number(String(e.fecha).slice(0, 4)) || 2026}
                          onChange={(fecha) => {
                            const menor18 = fecha ? edadEn(fecha) < 18 : false;
                            edit(i, {
                              fecha_nacimiento: fecha,
                              ...(menor18 ? { menor: true } : {}),
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`grupo-${i}`}>
                          Club o grupo <span className="font-normal text-muted-foreground">(opcional)</span>
                        </Label>
                        <Input
                          id={`grupo-${i}`}
                          className="mt-2 h-11"
                          placeholder="Ej.: C.A. Sigüenza"
                          value={p.grupo}
                          maxLength={100}
                          onChange={(ev) => edit(i, { grupo: ev.target.value })}
                        />
                      </div>
                    </div>
                    <p className="-mt-3 text-xs text-muted-foreground">
                      Regalo por clubes: una paletilla por cada 10 participantes del mismo club.
                    </p>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={p.menor}
                        onCheckedChange={(v) => edit(i, { menor: v === true })}
                      />
                      Es menor de edad
                    </label>
                    {p.menor && (
                      <div className="space-y-3 rounded-lg bg-secondary/50 p-4">
                        <Label htmlFor={`tutor-${i}`}>Adulto responsable (nombre y apellidos)</Label>
                        <Input
                          id={`tutor-${i}`}
                          className="h-11 bg-background"
                          value={p.tutor}
                          maxLength={120}
                          onChange={(ev) => edit(i, { tutor: ev.target.value })}
                        />
                        <label className="flex items-start gap-2 text-sm">
                          <Checkbox
                            checked={p.autorizacion}
                            onCheckedChange={(v) => edit(i, { autorizacion: v === true })}
                          />
                          <span>Soy su madre, padre o tutor y autorizo su participación.</span>
                        </label>
                        {e.autorizacion_menores && (
                          <details className="text-xs text-muted-foreground">
                            <summary className="cursor-pointer">Leer autorización completa</summary>
                            <p className="mt-2 whitespace-pre-wrap">{e.autorizacion_menores}</p>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              ))}
              <button
                type="button"
                disabled={ps.length >= 30}
                onClick={() => {
                  setPs((v) => [...v, nuevaPersona()]);
                  setRequestId("");
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-4 text-sm font-medium text-primary hover:border-primary hover:bg-primary/5 disabled:opacity-50"
              >
                <Plus size={18} />
                Añadir otra persona
              </button>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
              <h3 className="font-sans text-base font-semibold">Contacto de la inscripción</h3>
              <p className="text-sm text-muted-foreground">
                Hemos puesto el nombre de la primera persona inscrita. Cámbialo solo si el contacto
                es otra persona (por ejemplo, el padre o la madre de un menor).
              </p>
              {(["nombre", "email", "telefono"] as const).map((k) => (
                <div key={k}>
                  <Label htmlFor={`contact-${k}`}>
                    {k === "nombre"
                      ? "Nombre y apellidos"
                      : k === "email"
                        ? "Correo electrónico"
                        : "Teléfono"}
                  </Label>
                  <Input
                    className="mt-2 h-11"
                    id={`contact-${k}`}
                    type={k === "email" ? "email" : k === "telefono" ? "tel" : "text"}
                    autoComplete={k === "nombre" ? "name" : k === "email" ? "email" : "tel"}
                    maxLength={k === "email" ? 254 : k === "telefono" ? 25 : 120}
                    value={contact[k]}
                    onChange={(ev) => {
                      setContact((v) => ({ ...v, [k]: ev.target.value }));
                      setRequestId("");
                    }}
                  />
                </div>
              ))}
              <p className="text-sm text-muted-foreground">
                La confirmación de todas las personas llegará a este correo.
              </p>
            </div>
          )}
          {step === 2 && (
            <div>
              <h3 className="font-sans text-base font-semibold">Resumen de participantes</h3>
              <ul className="mt-4 divide-y">
                {ps.map((p, i) => (
                  <li key={i} className="flex justify-between gap-3 py-4 text-sm">
                    <div>
                      <strong>{p.nombre}</strong>
                      <p className="mt-1 text-muted-foreground">
                        {modalidadLabels[p.modalidad]}
                        {p.grupo ? ` · ${p.grupo}` : ""}
                        {p.fecha_nacimiento ? ` · ${edadEn(p.fecha_nacimiento)} años` : ""}
                        {p.menor ? " · Menor" : ""}
                      </p>
                    </div>
                    <span>
                      {euro(
                        Number(
                          p.modalidad === "acompanante"
                            ? e.precio_acompanante
                            : e.precio_participante,
                        ),
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-sm">
                Confirmación para <strong>{contact.email}</strong>
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Comida incluida para las {ps.length} personas.
              </p>
              <div className="mt-5"><CarreraBeneficios /></div>
              {e.condiciones && (
                <details className="mt-6 border-t py-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Condiciones de la inscripción
                  </summary>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
                    {e.condiciones}
                  </p>
                </details>
              )}
              {e.privacidad && (
                <details className="border-t py-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Privacidad y responsable
                  </summary>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
                    {e.responsable_legal}
                    {"\n\n"}
                    {e.privacidad}
                  </p>
                </details>
              )}
              <label className="mt-5 flex items-start gap-3 text-sm">
                <Checkbox
                  checked={accept}
                  onCheckedChange={(v) => setAccept(v === true)}
                  disabled={!open}
                />
                <span>
                  He leído y acepto el{" "}
                  <a href={reglamento.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    reglamento de la prueba
                  </a>
                  , las condiciones de la inscripción y la información de privacidad.
                </span>
              </label>
              {!open && (
                <p className="mt-3 border-l-4 border-accent bg-secondary/50 p-3 text-sm">
                  Las inscripciones y el pago online todavía no están abiertos. Podrás aceptar las
                  condiciones y pagar en cuanto la organización los active.
                </p>
              )}
            </div>
          )}
          <input
            aria-hidden="true"
            tabIndex={-1}
            className="hidden"
            value={website}
            onChange={(ev) => setWebsite(ev.target.value)}
            autoComplete="off"
          />
          {error && (
            <p role="alert" className="mt-5 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="mt-7 flex flex-wrap items-center justify-between gap-5 border-t pt-6">
            <div>
              <p className="text-xs text-muted-foreground">Total · comida incluida</p>
              <strong className="mt-1 block text-2xl text-primary">{euro(total)}</strong>
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <Button
                  variant="ghost"
                  aria-label="Volver al paso anterior"
                  onClick={() => {
                    setStep((v) => v - 1);
                    setError("");
                  }}
                  disabled={busy}
                >
                  <ArrowLeft />
                </Button>
              )}
              {step < 2 ? (
                <Button onClick={next}>
                  Continuar <ArrowRight />
                </Button>
              ) : (
                <Button onClick={pay} disabled={!open || busy || !accept}>
                  {busy ? "Conectando…" : "Pagar inscripción"}
                  <ArrowRight />
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
